import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';


const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture referral code from URL (?ref=CODE) and persist through signup flow
  const referralCode = searchParams.get('ref') || sessionStorage.getItem('referral_code') || null;

  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') setSessionExpired(true);
    const ref = searchParams.get('ref');
    if (ref) sessionStorage.setItem('referral_code', ref);
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let result;
    if (isLogin) {
      result = await login(formData.email, formData.password);
    } else {
      result = await register({ ...formData, referral_code: referralCode || undefined });
      if (result.success) sessionStorage.removeItem('referral_code');
    }

    setLoading(false);

    if (result.success) {
      navigate('/select-store');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left Panel — Form */}

        <div className="login-form-panel">
          <div className="login-form-inner">
            {/* Logo */}
            <div className="login-logo">
              <div className="login-logo-wrap">
                <img src={logo} alt="Aiviora" className="login-logo-img" />
              </div>
              <span className="login-logo-text">AivioraOps</span>
            </div>



            {/* Heading */}
            <h1 className="login-heading">
              {isLogin ? 'Welcome Back' : 'Get Started Now'}
            </h1>
            <p className="login-subtext">
              {isLogin
                ? 'Enter your credentials to access your account'
                : 'Create your account to get started'}
            </p>

            {sessionExpired && (
              <div className="alert alert-info" style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE' }}>
                Your session has expired. Please log in again.
              </div>
            )}

            {error && (
              <div className="alert alert-danger">{error}</div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div className="login-field">
                  <label>Name</label>
                  <input
                    type="text"
                    name="full_name"
                    placeholder="Enter your name"
                    value={formData.full_name}
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="login-field">
                <label>Email address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="login-field">
                <label>Password</label>
                <div className="login-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="login-forgot-row">
                  <button type="button" className="login-forgot-link">
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && (
                <p className="login-legal-note">
                  By signing up you agree to our{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
                  {' '}and{' '}
                  <a href="/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="login-submit-btn"
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, marginBottom: 0, marginRight: 8 }} />
                    Processing...
                  </>
                ) : isLogin ? 'Login' : 'Create Account'}
              </button>
            </form>

            {/* Toggle */}
            <p className="login-toggle-text">
              {isLogin ? "Don't have an account?" : 'Have an account?'}
              <button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }}>
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>

            {/* Copyright */}
            <p className="login-copyright">
              &copy; {new Date().getFullYear()} Aiviora. All rights reserved.
            </p>
            <p className="login-support-links">
              Need help? WhatsApp: +974 7085 8175 | Email: teamaiviora@gmail.com
              <br />
              <span>Built by <a href="https://www.linkedin.com/in/ridha-mariyam/" target="_blank" rel="noopener noreferrer">Ridha Mariyam</a></span>
            </p>
            <div className='builder-info'>
              
            </div>
          </div>
        </div>

        {/* Right Panel — Marketing */}
        <div className="login-brand-panel">
          <div className="login-brand-content">
            <h2 className="login-brand-heading">
              The simplest way to manage your operations
            </h2>
            <p className="login-brand-subtext">
              Streamline inventory, sales, and rentals in one powerful platform built for growing businesses.
            </p>

            {/* Dashboard Mockup Card */}
            <div className="login-mockup-card">
              {/* KPI Row */}
              <div className="mockup-kpi-row">
                <div className="mockup-kpi">
                  <div className="mockup-kpi-label">Revenue</div>
                  <div className="mockup-kpi-value">$24,500</div>
                  <div className="mockup-kpi-trend up">+12.5%</div>
                </div>
                <div className="mockup-kpi">
                  <div className="mockup-kpi-label">Orders</div>
                  <div className="mockup-kpi-value">1,248</div>
                  <div className="mockup-kpi-trend up">+8.2%</div>
                </div>
                <div className="mockup-kpi">
                  <div className="mockup-kpi-label">Customers</div>
                  <div className="mockup-kpi-value">573</div>
                  <div className="mockup-kpi-trend up">+4.1%</div>
                </div>
              </div>

              {/* Chart Area */}
              <div className="mockup-chart">
                <div className="mockup-chart-title">Revenue Overview</div>
                <svg viewBox="0 0 280 80" className="mockup-chart-svg">
                  <defs>
                    <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6C63FF" stopOpacity="0.2"/>
                      <stop offset="100%" stopColor="#6C63FF" stopOpacity="0.02"/>
                    </linearGradient>
                  </defs>
                  <path d="M0 60 Q30 55 60 45 T120 35 T180 25 T240 30 T280 20" fill="none" stroke="#6C63FF" strokeWidth="2"/>
                  <path d="M0 60 Q30 55 60 45 T120 35 T180 25 T240 30 T280 20 V80 H0 Z" fill="url(#chartGrad)"/>
                </svg>
              </div>

              {/* Mini Table */}
              <div className="mockup-table">
                <div className="mockup-table-header">
                  <span>Product</span><span>Sales</span><span>Revenue</span>
                </div>
                <div className="mockup-table-row">
                  <span>Wedding Dress A</span><span>42</span><span>$8,400</span>
                </div>
                <div className="mockup-table-row">
                  <span>Evening Gown B</span><span>38</span><span>$7,600</span>
                </div>
                <div className="mockup-table-row">
                  <span>Accessories Set</span><span>65</span><span>$3,250</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom logos */}
          <div className="login-brand-logos">
            <span>Trusted by 500+ businesses worldwide</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
